import React, { ComponentType, JSX, useCallback, useEffect, useState } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';

import { withAuthenticationRequired } from '@auth0/auth0-react';
import { isEqual } from 'lodash';

import { EvSpinner, PendoService } from '@evinced-private/ui-common';

import { ApiErrorHelper } from '../../helpers/ApiErrorHelper';
import { isCI } from '../../helpers/EnvironmentHelper';
import { getInvitationTokenFromUrl } from '../../helpers/InvitationTokenHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { IServerError } from '../../interfaces/ServerError';
import {
	getEvincedUserId,
	getUserEmail,
	getUserEmailDomain,
	isLoggedIn
} from '../../services/AuthenticationService';
import { logger } from '../../services/Logger';
import { setRedirectToPath } from '../../services/LoginRedirectionService';
import {
	getUserTenant,
	hasAccessToProduct as checkIfUserHasAccessToProduct
} from '../../services/TenantsService';
import { getAllUserPendingInvitations } from '../../services/UserInvitationService';
import { FullPageError } from '../full-page-error/FullPageError';

const pendoApiKey = import.meta.env.VITE_PENDO_API_KEY;
let hasInitializedPendo = false;

const getCurrentRelativeUrl = (): string => {
	let url = window.location.pathname;
	const params = window.location.search;
	const urlParams = new URLSearchParams(params);
	const searchParams = urlParams.toString();
	if (searchParams) {
		url += `?${searchParams}`;
	}
	return url;
};

const saveCurrentUrlForRedirection = async (): Promise<void> => {
	const redirectToUrl = getCurrentRelativeUrl();
	setRedirectToPath(redirectToUrl);
};

/**
 *
 * @returns {hasTenant: boolean, hasAccessToProduct: boolean,
 *  hasPendingInvitations: boolean }
 */
const getUserStatus = async (): Promise<{
	hasTenant: boolean;
	hasAccessToProduct: boolean;
	hasPendingInvitations: boolean;
}> => {
	const userTenant = await getUserTenant();
	const userHasAccessToProduct = userTenant
		? await checkIfUserHasAccessToProduct(userTenant)
		: false;
	const invitaitonTokenFromUrl = getInvitationTokenFromUrl();
	const pendingInvitations = await getAllUserPendingInvitations();

	return {
		hasTenant: !!userTenant,
		hasAccessToProduct: userHasAccessToProduct,
		hasPendingInvitations: !!invitaitonTokenFromUrl || pendingInvitations.length > 0
	};
};

const initializePendo = (): void => {
	if (isCI() || hasInitializedPendo) {
		return;
	}

	PendoService.init(
		{
			visitorId: getEvincedUserId(),
			email: getUserEmail(),
			accountId: getUserEmailDomain()
		},
		pendoApiKey,
		logger
	);
	hasInitializedPendo = true;
};

type TPrivateRouteProps = RouteProps & {
	component: ComponentType<RouteComponentProps>;
};

function PrivateRouteInner({
	component: RouteComponent,
	...rest
}: TPrivateRouteProps): JSX.Element {
	const [isLoading, setLoading] = useState(true);
	const [userStatus, setUserStatus] = useState({
		hasTenant: false,
		hasAccessToProduct: false,
		hasPendingInvitations: false
	});
	const [error, setError] = useState<string | null>(null);
	const { location } = rest;

	const checkIfUserHasTenant = useCallback(async () => {
		// short circuit - no need to check if there's a tenant on each route if we know they have
		if (userStatus.hasTenant && userStatus.hasAccessToProduct) {
			return;
		}
		setLoading(true);

		try {
			const updatedUserStatus = await getUserStatus();
			if (!isEqual(updatedUserStatus, userStatus)) {
				setUserStatus(updatedUserStatus);
			}
		} catch (error) {
			logger.error('Error getting tenant', error);
			setError(
				`An error occurred while retrieving your account information. ${ApiErrorHelper.getErrorMessage(
					error as unknown as IServerError
				)}`
			);
		} finally {
			setLoading(false);
		}
	}, [userStatus]);

	useEffect(() => {
		checkIfUserHasTenant();
	}, [checkIfUserHasTenant]);

	useEffect(() => {
		if (!isLoading && !error) {
			initializePendo();
		}
	}, [error, isLoading]);

	if (isLoading) {
		return <EvSpinner />;
	}
	if (error) {
		return <FullPageError title="Product Loading Error" />;
	}
	return (
		<Route
			{...rest}
			location={location}
			render={(props) => {
				if (!isLoggedIn()) {
					return (
						<Redirect
							to={{
								pathname: '/login',
								state: { from: props.location },
								search: props.location.search
							}}
						/>
					);
				}

				// handling account activation and product invitation
				const pathname = location?.pathname ?? window.location.pathname;
				const { hasTenant, hasAccessToProduct, hasPendingInvitations } = userStatus;

				if (!hasTenant || !hasAccessToProduct) {
					let redirectTo = '';
					if (hasTenant && !hasAccessToProduct) {
						redirectTo = RoutesHelper.getProductActivationPage();
					} else if (hasPendingInvitations) {
						redirectTo = RoutesHelper.getInvitationActivationPage();
					} else {
						redirectTo = RoutesHelper.getNoTenantPage();
					}
					// no need for redirect if the user is already in that path
					if (pathname !== redirectTo) {
						return (
							<Redirect
								to={{
									pathname: redirectTo,
									state: { from: props.location },
									search: props.location.search
								}}
							/>
						);
					}
				}
				return <RouteComponent {...props} />;
			}}
		/>
	);
}

export const PrivateRoute = withAuthenticationRequired(PrivateRouteInner, {
	onBeforeAuthentication: saveCurrentUrlForRedirection,
	onRedirecting: () => {
		return <EvSpinner />;
	}
});
